import React, { useEffect, useRef, useState } from 'react';
import { Upload, X, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { getMyBrand, setMyBrand } from '../../lib/api';

/**
 * BrandSettingsCard — lets any user persist their own brand label + logo so
 * the carousel generator and other PDF exports pre-fill these values without
 * forcing them to upload a logo every time.
 */
export default function BrandSettingsCard() {
  const [brandLabel, setBrandLabel] = useState('');
  const [brandLogo, setBrandLogo] = useState(''); // data URL
  const [initial, setInitial] = useState({ brandLabel: '', brandLogo: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  // Initial load — runs once on mount via canonical ref-init pattern.
  const loaderRef = useRef(null);
  if (loaderRef.current == null) {
    loaderRef.current = getMyBrand()
      .then((data) => {
        const next = {
          brandLabel: data?.brand_label || '',
          brandLogo: data?.brand_logo || '',
        };
        setBrandLabel(next.brandLabel);
        setBrandLogo(next.brandLogo);
        setInitial(next);
      })
      .catch(() => {
        toast.error('Could not load brand profile');
      })
      .finally(() => setLoading(false));
  }

  // Keep typescript-y dirty state clean
  useEffect(() => () => { loaderRef.current = null; }, []);

  const dirty = brandLabel !== initial.brandLabel || brandLogo !== initial.brandLogo;

  function handleLogoFile(file) {
    if (!file) return;
    if (!/^image\/(png|jpe?g|svg\+xml|webp)$/.test(file.type)) {
      toast.error('Please upload a PNG, JPG, SVG, or WebP image');
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      toast.error('Logo must be under 4 MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => setBrandLogo(e.target?.result || '');
    reader.onerror = () => toast.error('Could not read file');
    reader.readAsDataURL(file);
  }

  async function handleSave() {
    if (saving || !dirty) return;
    setSaving(true);
    try {
      await setMyBrand({
        brandLabel: brandLabel.slice(0, 60),
        brandLogo: brandLogo || '',
      });
      setInitial({ brandLabel, brandLogo });
      toast.success('Brand profile saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Could not save brand');
    } finally {
      setSaving(false);
    }
  }

  function handleReset() {
    setBrandLabel(initial.brandLabel);
    setBrandLogo(initial.brandLogo);
  }

  return (
    <Card className="border-border">
      <CardContent className="p-6 space-y-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold">Brand profile</h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              Used to brand your PDF exports (carousels, audits, posts).
              You can override these per-export anytime.
            </p>
          </div>
          {dirty && (
            <span
              className="text-[10px] font-mono uppercase tracking-wider text-amber-600 dark:text-amber-400 px-2 py-1 rounded-md bg-amber-500/10 border border-amber-500/30"
              data-testid="brand-settings-dirty"
            >
              Unsaved
            </span>
          )}
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        ) : (
          <>
            {/* Brand label */}
            <div className="space-y-1.5">
              <Label htmlFor="brand-settings-label" className="text-xs">Brand name</Label>
              <Input
                id="brand-settings-label"
                value={brandLabel}
                onChange={(e) => setBrandLabel(e.target.value.slice(0, 60))}
                placeholder="e.g. Acme Capital · Saurabh Singh"
                disabled={saving}
                data-testid="brand-settings-label-input"
              />
              <p className="text-[10px] text-muted-foreground">
                Shown in header / footer of branded PDF exports.
              </p>
            </div>

            {/* Brand logo */}
            <div className="space-y-2">
              <Label className="text-xs">Logo (optional)</Label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                className="hidden"
                onChange={(e) => handleLogoFile(e.target.files?.[0])}
                data-testid="brand-settings-logo-file"
              />
              {brandLogo ? (
                <div className="flex items-center gap-3 rounded-md border border-border bg-muted/30 px-3 py-2">
                  <img
                    src={brandLogo}
                    alt="brand logo"
                    className="h-12 w-auto max-w-[140px] object-contain rounded-sm"
                  />
                  <div className="flex-1 text-xs text-muted-foreground">
                    Logo set · used in PDF header & footer
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={saving}
                    className="gap-1.5"
                    data-testid="brand-settings-logo-replace"
                  >
                    <Upload size={13} /> Replace
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setBrandLogo('')}
                    disabled={saving}
                    className="h-8 w-8"
                    data-testid="brand-settings-logo-remove"
                  >
                    <X size={14} />
                  </Button>
                </div>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={saving}
                  data-testid="brand-settings-logo-upload"
                >
                  <Upload size={14} /> Upload logo (PNG / JPG / SVG / WebP, max 4 MB)
                </Button>
              )}
              <p className="text-[10px] text-muted-foreground">
                PNG with a transparent background works best. Saved to your
                account so you do not need to re-upload it.
              </p>
            </div>

            {/* Save / Reset */}
            <div className="flex items-center gap-2 pt-1">
              <Button
                onClick={handleSave}
                disabled={!dirty || saving}
                className="gap-1.5"
                data-testid="brand-settings-save"
              >
                {saving ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Check size={14} />
                )}
                {saving ? 'Saving…' : 'Save brand profile'}
              </Button>
              {dirty && !saving && (
                <Button
                  variant="ghost"
                  onClick={handleReset}
                  className="gap-1.5"
                  data-testid="brand-settings-reset"
                >
                  Reset
                </Button>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
